import { useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { motion } from 'framer-motion';
import { X, Copy, Download, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * QrCodeModal — displays a scannable QR for any URL, with download-as-PNG
 * and copy-URL affordances. Used by Organizations for printable posters.
 */
export default function QrCodeModal({ url, title, subtitle, onClose }) {
  const svgRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* silent */ }
  };

  const downloadPng = () => {
    const svg = svgRef.current?.querySelector('svg');
    if (!svg) { toast.error('QR not ready — try again in a moment.'); return; }
    // Serialize SVG → rasterize on canvas → download PNG
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);
    const b64 = window.btoa(unescape(encodeURIComponent(svgString)));
    const dataUrl = `data:image/svg+xml;base64,${b64}`;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const size = 1024; // high-res, print-friendly
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 32, 32, size - 64, size - 64);
      const png = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = png;
      a.download = `${(title || 'wildquest').replace(/\s+/g, '-').toLowerCase()}-qr.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('QR code downloaded');
    };
    img.onerror = () => toast.error('Could not export QR');
    img.src = dataUrl;
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="qr-modal"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-[#0A1712] border border-emerald-500/25 rounded-3xl p-5 space-y-4"
      >
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-emerald-300">Print & share</p>
            <p className="font-serif text-lg truncate">{title || 'Scan this'}</p>
            {subtitle && <p className="text-[11px] text-muted-foreground truncate">{subtitle}</p>}
          </div>
          <button onClick={onClose} data-testid="qr-close-btn" className="p-2 rounded-full hover:bg-white/10">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div
          ref={svgRef}
          className="mx-auto p-4 bg-white rounded-2xl w-fit"
          data-testid="qr-canvas"
        >
          <QRCodeSVG
            value={url}
            size={220}
            fgColor="#0A1712"
            bgColor="#ffffff"
            level="M"
            includeMargin={false}
            marginSize={0}
          />
        </div>

        <div className="px-2 py-2 bg-black/40 border border-white/10 rounded-lg text-[10px] font-mono text-emerald-200 break-all text-center">
          {url}
        </div>

        <div className="flex gap-2">
          <button
            onClick={copy}
            data-testid="qr-copy-btn"
            className="flex-1 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 text-xs flex items-center justify-center gap-1.5"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied' : 'Copy URL'}
          </button>
          <button
            onClick={downloadPng}
            data-testid="qr-download-btn"
            className="flex-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-semibold py-2.5 text-xs flex items-center justify-center gap-1.5"
          >
            <Download className="w-3.5 h-3.5" /> Download PNG
          </button>
        </div>

        <p className="text-[10px] text-muted-foreground text-center">
          High-resolution PNG (1024×1024). Perfect for posters, business cards and study reports.
        </p>
      </motion.div>
    </div>
  );
}
